import React, { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as am5 from "@amcharts/amcharts5";
import * as am5map from "@amcharts/amcharts5/map";
import am5geodata_mexicoLow from "@amcharts/amcharts5-geodata/mexicoLow";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

const MexicoMap = ({ chartData }) => {

  const [t] = useTranslation("dashboard");

  const chartRef = useRef(null);
  const [legendData, setLegendData] = useState([]);

  useLayoutEffect(() => {
    if (!chartData?.data) return;

    const root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5map.MapChart.new(root, {
        panX: "none",
        panY: "none",
        wheelY: "none",
        projection: am5map.geoMercator(),
      })
    );

    const polygonSeries = chart.series.push(
      am5map.MapPolygonSeries.new(root, {
        geoJSON: am5geodata_mexicoLow,
        valueField: "value",
        calculateAggregates: true,
      })
    );

    const mappedData = chartData.data.map((item) => ({
      id: item.region,
      value: item.count,
      grupo: item.grupo,
    }));

    polygonSeries.data.setAll(mappedData);

    polygonSeries.mapPolygons.template.setAll({
      tooltipText: "{name}: {value}",
      interactive: true,
    });

    polygonSeries.mapPolygons.template.states.create("hover", {
      fill: am5.color(0x677935),
    });

    // 1. Calcular promedios por grupo
    const grupos = {};
    mappedData.forEach(({ grupo, value }) => {
      if (!grupo) return;
      if (!grupos[grupo]) {
        grupos[grupo] = { total: 0, count: 0 };
      }
      grupos[grupo].total += value;
      grupos[grupo].count += 1;
    });

    const promedios = Object.entries(grupos).map(([grupo, { total, count }]) => ({
      grupo,
      promedio: total / count,
    }));

    // 2. Ordenar por promedio
    promedios.sort((a, b) => a.promedio - b.promedio);

    // 3. Asignar colores (verde, amarillo, naranja)
    const escalaColores = [am5.color(0x4caf50), am5.color(0xffeb3b), am5.color(0xff9800)];
    const grupoColors = {};
    promedios.forEach((item, index) => {
      grupoColors[item.grupo] = escalaColores[index] || am5.color(0xcccccc);
    });

    // Guardar info para la leyenda (React state)
    setLegendData(
      promedios.map((item, index) => ({
        grupo: item.grupo,
        color: escalaColores[index]?.toCSSHex() || "#cccccc",
        promedio: item.promedio.toFixed(2),
      }))
    );

    // 4. Pintar el mapa
    polygonSeries.events.on("datavalidated", function () {
      const colorSet = am5.ColorSet.new(root, { step: 2 });
      let colorIndex = 0;

      polygonSeries.mapPolygons.each((polygon) => {
        const dataItem = polygon.dataItem?.dataContext;
        const grupo = dataItem?.grupo;
        const value = dataItem?.value;

        if (!value || value === 0) {
          polygon.setAll({
            fill: am5.color(0xcccccc),
            stroke: am5.color(0xffffff),
          });
        } else if (grupo && grupoColors[grupo]) {
          polygon.setAll({
            fill: grupoColors[grupo],
            stroke: am5.color(0xffffff),
          });
        } else {
          polygon.setAll({
            fill: colorSet.getIndex(colorIndex),
            stroke: am5.color(0xffffff),
          });
          colorIndex++;
        }
      });
    });

    return () => root.dispose();
  }, [chartData]);

  return (
    <>
      <div ref={chartRef} style={{ width: "100%", height: "500px" }} />
      {legendData.length > 0 && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
          {legendData.map((item) => (
            <div
              key={item.grupo}
              style={{
                display: "flex",
                alignItems: "center",
                marginRight: "1.5rem",
              }}
            >
              <div
                style={{
                  width: 20,
                  height: 20,
                  backgroundColor: item.color,
                  marginRight: 8,
                  border: "1px solid #999",
                }}
              ></div>
              <span>
              {t("maps.mexico.legend_group")} {item.grupo} ({t("maps.mexico.legend_average")}: {item.promedio})
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default MexicoMap;
